import {
  apiRequest,
  clearStoredAuthSession,
  getAuthToken,
  getStoredRefreshToken,
  storeAuthTokens,
} from "./apiClient";

const normalizeUserProfile = (user = {}) => {
  if (!user || typeof user !== "object") return user;

  const profileImage =
    (typeof user.profileImage === "string" && user.profileImage.trim()) ||
    (typeof user.profile_image === "string" && user.profile_image.trim()) ||
    (typeof user.image === "string" && user.image.trim()) ||
    (typeof user.avatar === "string" && user.avatar.trim());

  return {
    ...user,
    profileImage,
  };
};

const extractAuthPayload = (response) => {
  const payload = response?.data ?? response;
  const user = payload?.user || payload?.admin || payload?.profile || payload;
  const token =
    payload?.token ||
    payload?.accessToken ||
    payload?.access_token ||
    payload?.jwt ||
    payload?.authToken;
  const refreshToken = payload?.refreshToken || payload?.refresh_token;
  const role = user?.role || payload?.role;

  return {
    payload,
    user: normalizeUserProfile(user),
    token,
    refreshToken,
    role,
  };
};

export const loginAdmin = async ({ email, password }) => {
  const response = await apiRequest("/auth/login", {
    method: "POST",
    skipAuthRedirect: true,
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ email, password, role: "admin" }),
  });

  return extractAuthPayload(response);
};

export const loginAccount = async ({ email, password, role }) => {
  const response = await apiRequest("/auth/login", {
    method: "POST",
    skipAuthRedirect: true,
    headers: { "Content-Type": "application/json" },
    // One identifier may be used for both a customer and a driver account.
    // The selected role keeps authentication scoped to the intended account.
    body: JSON.stringify({ email, password, ...(role ? { role } : {}) }),
  });

  return extractAuthPayload(response);
};

export const changePassword = ({ currentPassword, newPassword }) =>
  apiRequest("/auth/change-password", {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ currentPassword, newPassword }),
  });

export const requestPasswordResetOtp = ({ email }) =>
  apiRequest("/auth/forgot-password", {
    method: "POST",
    skipAuthRedirect: true,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });

export const verifyPasswordResetOtp = async ({ email, otp }) => {
  const response = await apiRequest("/auth/verify-otp", {
    method: "POST",
    skipAuthRedirect: true,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, otp }),
  });
  const payload = response?.data ?? response;

  return payload?.token || payload?.resetToken || payload?.reset_token;
};

export const resetPassword = ({ token, newPassword }) =>
  apiRequest("/auth/reset-password", {
    method: "POST",
    skipAuthRedirect: true,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token, newPassword }),
  });

export const fetchUserProfile = async () => {
  const response = await apiRequest("/users/profile");
  const profile = response?.data ?? response;

  return normalizeUserProfile(profile);
};

export const saveAuthSession = ({ token, refreshToken, user, role }) => {
  if (token || refreshToken) {
    storeAuthTokens({ accessToken: token, refreshToken });
  }

  if (user && typeof user === "object") {
    localStorage.setItem("authUser", JSON.stringify(user));
  }

  if (role) {
    localStorage.setItem("userRole", role);
  }

  localStorage.setItem("isLoggedIn", "true");
};

export const clearAuthSession = () => {
  clearStoredAuthSession();
};

export const hasValidAuthSession = () => {
  const isLoggedIn = localStorage.getItem("isLoggedIn") === "true";
  const role = localStorage.getItem("userRole");
  const token = getAuthToken();
  const refreshToken = getStoredRefreshToken();

  if (!isLoggedIn || role !== "admin" || !token || !refreshToken) {
    clearAuthSession();
    return false;
  }

  return true;
};
